// eslint-disable-next-line import/no-extraneous-dependencies
import { FormattedShopifyCollection } from '@portal/types/shopify/product.formatted.types';
import { create } from 'zustand';
import { BundleVariant, CollectionVariant, Variant } from './types';

export type PDPType = 'productView' | 'bundleView' | 'otpView' | null;

interface ProductState {
  pdpType: PDPType;
  product: {
    id: string;
    name: string;
    price: number;
    description: string;
    imageUrl: string;
  } | null;
  quantity: number;
  flavor: string | null;
  flavorSubtitle: string;
  checkoutLoading: boolean;
  checkoutDisabled: boolean;
  selectedVariantId: string;
  isOtpProduct: boolean;
  productCadence: string;
  variants: Variant[] | null;
  collectionVariants: CollectionVariant[] | null;
  bundleVariant: BundleVariant | null;
  bundleData: FormattedShopifyCollection | null;
  lastViewContentId: string;
  setProductCadence: (productCadence: string) => void;
  setSelectedVariantId: (selectedVariantId: string) => void;
  setProduct: (product: ProductState['product']) => void;
  setQuantity: (quantity: number | undefined) => void;
  setFlavor: (flavor: string) => void;
  setFlavorSubtitle: (flavorSubtitle?: string) => void;
  setCheckoutLoading: (checkoutLoading: boolean) => void;
  setCheckoutDisabled: (checkoutDisabled: boolean) => void;
  selectedPdpProductTitle: string;
  setSelectedPdpProductTitle: (selectedPdpProductTitle: string) => void;
  selectedPdpProductCategory: string;
  selectedPdpProductCategoryUrl: string;
  setIsOtpProduct: (isOtpProduct: boolean) => void;
  setVariants: (variants: ProductState['variants']) => void;
  setCollectionVariants: (collectionVariants: ProductState['collectionVariants']) => void;
  setPdpType: (pdpType: PDPType) => void;
  setBundleVariant: (bundleVariant: ProductState['bundleVariant']) => void;
  setBundleData: (bundleData: ProductState['bundleData']) => void;
  setLastViewContentId: (lastViewContentId: string) => void;
  resetAll: () => void;
}

export const useProductStore = create<ProductState>((set) => ({
  pdpType: null,
  product: null,
  quantity: 1,
  flavor: null,
  flavorSubtitle: '',
  checkoutLoading: false,
  checkoutDisabled: false,
  selectedVariantId: '',
  isOtpProduct: false,
  lastViewContentId: '',
  setProduct: (product: any) => set({ product }),
  setQuantity: (q) => set((s) => (s.quantity === q ? s : { quantity: q })),
  setSelectedVariantId: (selectedVariantId: string) =>
    set((s) => (s.selectedVariantId === selectedVariantId ? {} : { selectedVariantId })),
  setFlavor: (flavor: string) => set({ flavor }),
  setFlavorSubtitle: (flavorSubtitle: string) => set({ flavorSubtitle }),
  setCheckoutLoading: (checkoutLoading: boolean) => set({ checkoutLoading }),
  setCheckoutDisabled: (checkoutDisabled: boolean) => set({ checkoutDisabled }),
  selectedPdpProductTitle: 'AG1 Product',
  setSelectedPdpProductTitle: (selectedPdpProductTitle: string) =>
    set((s) =>
      s.selectedPdpProductTitle === selectedPdpProductTitle ? {} : { selectedPdpProductTitle }
    ),
  selectedPdpProductCategory: 'Shop All',
  selectedPdpProductCategoryUrl: '/shop',
  productCadence: '',
  setIsOtpProduct: (isOtpProduct: boolean) => set({ isOtpProduct }),
  variants: null,
  selectedVariant: null,
  collectionVariants: null,
  bundleVariant: null,
  bundleData: null,
  setVariants: (variants: Variant[] | null) => set({ variants }),
  setCollectionVariants: (collectionVariants: CollectionVariant[] | null) =>
    set({ collectionVariants }),
  setPdpType: (t) => set((s) => (s.pdpType === t ? s : { pdpType: t })),
  setBundleVariant: (bundleVariant: BundleVariant | null) => set({ bundleVariant }),
  setBundleData: (bundleData: FormattedShopifyCollection | null) => set({ bundleData }),
  setProductCadence: (productCadence: string) => set({ productCadence }),
  setLastViewContentId: (lastViewContentId: string) => set({ lastViewContentId }),
  resetAll: () =>
    set({
      pdpType: null,
      product: null,
      quantity: 1,
      flavor: null,
      flavorSubtitle: '',
      checkoutLoading: false,
      checkoutDisabled: false,
      selectedVariantId: '',
      isOtpProduct: false,
      productCadence: '',
      variants: null,
      collectionVariants: null,
      bundleVariant: null,
      bundleData: null,
      lastViewContentId: '',
      selectedPdpProductTitle: 'AG1 Product',
      selectedPdpProductCategory: 'Shop All',
      selectedPdpProductCategoryUrl: '/shop'
    })
}));
